<template>
  <div class="activity-feed">
    <div class="row">
      <div class="col-md-12">
        <search-collapse v-if="hasSearch" table-id="activity">
          <activity-search :actionsToSearch="actionsToSearch"/>
        </search-collapse>
      </div>
    </div>
    <spinner :show="loading"/>
    <div v-if="!loading" v-for="activity in activities" :key="activity.id" class="feed-item">
      <div class="content-wrapper py-2 border-bottom">
        <div class="data-placeholder">
          <div class="d-flex">
            <img src='@/assets/no-user-image.jpg' class="img-fluid">
            <p class="main-info">
              {{ activity.created_by.display_name }} -
              <span class="date-placeholder text-muted">
              <font-awesome-icon :icon="'clock'" class="icon mr-1"></font-awesome-icon>
              <span>{{ activity.created_at | toDateTime }}</span>
            </span>
            </p>
          </div>
          <div class="secondary-info text-muted mb-2">
            <slot name="text" :activity="activity"></slot>
            <b-button v-if="showDetailsButton(activity)"
                      size="xs"
                      variant="primary"
                      @click="onDetailsButtonClick(activity)">
              {{ $t('Details') }}
            </b-button>
            <b-button v-if="showViewButton(actions[activity.action])"
                      size="xs"
                      class="ml-1"
                      variant="primary"
                      @click="onViewButtonClick(actions[activity.action], activity)">
              {{ $t('View') }}
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <no-data-available v-if="!loading && (!activities || activities.length === 0)" height="10"/>
    <div v-if="pagination && !loading" class="table-footer border-top pt-3">
      <div class="d-flex justify-content-between align-items-center">
        <showing-total-text v-if="pagination.total" :pagination="pagination"></showing-total-text>
        <paginator :pagination="pagination" @pageChange="onPageChange"/>
      </div>
    </div>
    <activity-details-modal :visible="showDetailsModal"
                            :activity="currentActivity"
                            :actions="actions"
                            :value-visualizer="valueVisualizer"
                            @hidden="onDetailsModalHidden">
      <template v-bind:slot="slotName" slot-scope="scope" v-for="(slot, slotName) in $scopedSlots">
        <slot v-bind:name="slotName" v-bind="scope"/>
      </template>
    </activity-details-modal>
  </div>
</template>

<script>
  import ActivityDetailsModal from "./ActivityDetailsModal";
  import _ from 'lodash';
  import Spinner from "@/core/components/common/Spinner";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import ShowingTotalText from "@/core/components/paginator/ShowingTotalText";
  import Paginator from "@/core/components/paginator/Paginator";
  import ActivitySearch from "@/modules/activities/ActivitySearch";
  import SearchCollapse from "@/core/components/search-collapse/SearchCollapse";
  import {createNamespacedHelpers} from "vuex";
  import {showActivityDetailsButton} from "@/modules/sales/view/activities/overrideDefaultActivitiesFields";

  const {mapState, mapActions} = createNamespacedHelpers('activities');

  export default {
    name: "ActivityList",
    components: {
      ActivitySearch,
      NoDataAvailable,
      Spinner,
      ActivityDetailsModal,
      ShowingTotalText,
      Paginator,
      SearchCollapse
    },
    props: {
      for: {
        type: String,
        required: true
      },
      valueVisualizer: {
        type: Function,
        default: (activity, attr, val) => val
      },
      actionsToHideDetailsButton: {
        type: Array,
        default: () => []
      },
      actions: {
        type: Object,
        required: true
      },
      actionsToSearch: {
        type: Array,
        default: () => []
      },
      hasSearch: {
        type: Boolean,
        default: false,
      }
    },
    computed: {
      ...mapState({
        activities: state => state.tableData[state.tableSource].activities,
        pagination: state => state.tableData[state.tableSource].pagination,
        loading: state => state.tableData[state.tableSource].loading,
      })
    },
    data() {
      return {
        currentActivity: null,
        showDetailsModal: false,
      };
    },
    methods: {
      ...mapActions(['getActivities', 'updateActivitiesLoading']),
      async onPageChange(page) {
        await this.getActivities(page);
      },
      showDetailsButton(activity) {
        return (!this.actionsToHideDetailsButton.includes(activity.action)
          && this.actions[activity.action]) || showActivityDetailsButton.includes(activity.action);
      },
      onDetailsButtonClick(activity) {
        this.currentActivity = _.cloneDeep(activity);
        this.showDetailsModal = true;
      },
      onDetailsModalHidden() {
        this.showDetailsModal = false;
      },
      showViewButton(action) {
        return action && action.urlName;
      },
      onViewButtonClick(action, activity) {
        this.$router.push({
          name: action.urlName,
          params: {id: activity.data[action.attributeName]},
        })
      },
    },
    async beforeMount() {
      await this.getActivities();
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../core/scss/variables";

  .activity-feed {
    padding: 1em;
    word-wrap: break-word;
  }

  .feed-item {
    position: relative;
    padding-left: 2em;
    border-left: 2px solid $primary;
    margin: 0;
    vertical-align: baseline;

    &:after {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: -6px;
      width: 10px;
      height: 10px;
      border-radius: 6px;
      background: #fff;
      border: 1px solid #4b5158;
    }

    .date-placeholder {
      display: inline-block;

      span {
        display: inline-block;
      }
    }

    .data-placeholder {
      img {
        height: 26px;
        width: 26px;
        border-radius: 50%;
        display: inline-block;
        float: left;
      }

      .main-info {
        float: left;
        padding-top: .1em;
        margin-left: 1em;
        margin-bottom: 0;
      }

      .secondary-info {
        display: block;
        padding-left: 2.9em;
      }
    }

  }
</style>
